import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CommerceProductView } from 'shared';
import { adminApi, onAdminTokenChange } from '@/api';
import { compareProducts, type ProductInput } from '@/utils/commerce-products';

export const useCommerceProductsStore = defineStore('commerce-products', () => {
  const products = ref<CommerceProductView[]>([]);
  const loading = ref(false);
  const error = ref('');
  const sortedProducts = computed(() => [...products.value].sort(compareProducts));
  let pending: Promise<boolean> | undefined;
  let sessionVersion = 0;

  function load(): Promise<boolean> {
    if (pending) return pending;
    loading.value = true;
    error.value = '';
    const version = sessionVersion;
    pending = (async () => {
      try {
        const result = await adminApi.get<CommerceProductView[]>('/admin/commerce/products');
        if (version !== sessionVersion) return false;
        products.value = result;
        return true;
      } catch {
        if (version === sessionVersion) error.value = '商品加载失败，请检查权限或网络后重试。';
        return false;
      } finally {
        if (version === sessionVersion) {
          loading.value = false;
          pending = undefined;
        }
      }
    })();
    return pending;
  }

  async function save(input: ProductInput): Promise<CommerceProductView> {
    await pending;
    const version = sessionVersion;
    const { id, ...body } = input;
    const saved = id
      ? await adminApi.patch<CommerceProductView>(`/admin/commerce/products/${id}`, body)
      : await adminApi.post<CommerceProductView>('/admin/commerce/products', body);
    if (version !== sessionVersion) throw new Error('管理员会话已变更，请重新进入商品管理。');
    const index = products.value.findIndex((item) => item.id === saved.id);
    if (index < 0) products.value.push(saved);
    else products.value.splice(index, 1, saved);
    return saved;
  }

  onAdminTokenChange((token) => {
    if (token) return;
    sessionVersion++;
    products.value = [];
    loading.value = false;
    error.value = '';
    pending = undefined;
  });
  return { products, sortedProducts, loading, error, load, save };
});
